import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Subscription, switchMap } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ModalController, PopoverController } from '@ionic/angular';
import { BetaFeedbackPage } from '../../modals/beta-feedback/beta-feedback.page';

import { DolbyService } from '../../services/dolby/dolby.service';
import { RecordingService } from '../../services/recording/recording.service';
import { SupportCenterService } from '../../services/support-center/support-center.service';
import { IPadService } from '../../services/ipad/ipad.service';
import { SessionsService } from '../../services/sessions/sessions.service';
import { UserService } from '../../services/user/user.service';
import { Webhook } from '@sc/types';
import { Session } from '@sc/types';
import { UserData } from '@sc/types';
import { AnalyticsService } from '../../services/analytics/analytics.service';

import * as dayjs from 'dayjs';
import * as arraySupport from 'dayjs/plugin/arraySupport';
import * as dayjsDuration from 'dayjs/plugin/duration';
import { AudioRecorderService } from '../../services/audio-recorder/audio-recorder.service';
import { VideoRecorderService } from '../../services/video-recorder/video-recorder.service';
import { RolesService } from '../../services/roles/roles.service';
import { RoleLimits, Roles } from '@sc/types';
import { ThemeService, defaultPopoverOptions } from '../../services/theme/theme.service';
import { InvitationLinkMenuComponent } from '../../menus/invitation-link-menu/invitation-link-menu.component';
import { StudioSidebarService } from '../../services/studio-sidebar/studio-sidebar.service';
import { LeaveSessionMenuComponent } from '../../menus/leave-session-menu/leave-session-menu.component';
import { DescriptService } from '../../services/descript/descript.service';
import { DailyService } from '../../services/daily/daily.service';

dayjs.extend(arraySupport);
dayjs.extend(dayjsDuration);

@Component({
  selector: 'sc-studio-nav',
  templateUrl: './studio-nav.component.html',
  styleUrls: ['./studio-nav.component.scss'],
})
export class StudioNavComponent implements OnInit, OnDestroy {
  studioShow$ = this.sessionsService.studioShow$;
  studioSession$ = this.sessionsService.studioSession$;
  studioPilotProgram$ = this.sessionsService.studioPilotProgram$;
  localParticipantDolby$ = this.dolbyService.localParticipant$;
  localParticipantDaily$ = this.dailyService.localParticipant$;
  audioWarnings$ = this.audioRecorderService.warnings$;
  videoWarnings$ = this.videoRecorderService.warnings$;
  role$ = this.rolesService.role$;
  autoplayBlocked$ = this.dolbyService.autoplayBlocked$;
  theme$ = this.themeService.theme$;
  descriptDrive$ = this.descriptService.descriptDrive$;
  dolbyConversation$ = this.dolbyService.dolbyConversation$;
  dailyCall$ = this.dailyService.dailyCall$;

  roleLimits = RoleLimits;
  ROLES = Roles;
  warnings: string;
  session: Session;
  user: UserData;
  onMobileDevice = false;
  recording: boolean;
  iPhoneiPadDevice = false;
  timer = '00:00:00';
  stageCount = 0;
  backstageCount = 0;
  participantsTooltip = '0 on stage';
  participantWebhooks: Array<Webhook> = [];
  toggleSidebar = this.studioSidebarService.toggleSidebar.bind(this.studioSidebarService);
  linkedToDescript = false;
  hasRecordings = false;

  subs: Array<Subscription> = [];

  constructor(
    public router: Router,
    private audioRecorderService: AudioRecorderService,
    private analyticsService: AnalyticsService,
    private deviceDetectorService: DeviceDetectorService,
    private dolbyService: DolbyService,
    private dailyService: DailyService,
    private iPadService: IPadService,
    private modalController: ModalController,
    private popoverController: PopoverController,
    private recordingService: RecordingService,
    private rolesService: RolesService,
    private sessionsService: SessionsService,
    private studioSidebarService: StudioSidebarService,
    private supportCenterService: SupportCenterService,
    private userService: UserService,
    private videoRecorderService: VideoRecorderService,
    private themeService: ThemeService,
    private descriptService: DescriptService
  ) {}

  /**
   * Initializes studio nav component
   */
  ngOnInit() {
    this.onMobileDevice = this.deviceDetectorService.isMobile();
    this.iPhoneiPadDevice = this.deviceDetectorService.getDeviceInfo().device === 'iPhone' || this.iPadService.check();
    this.setupUser();
    this.setupSession();
    this.setupRecording();
    this.setupTimer();
    this.setupWarnings();
    this.setupParticipantCounts();
    this.setupLinkedToDescript();
    this.setupRecordingCount();
  }

  playBlockedAudio() {
    this.dolbyService.playBlockedAudio();
  }

  async openInvite(click, defaultLocation = 'The Stage') {
    const menu = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: InvitationLinkMenuComponent,
      componentProps: { defaultLocation, session: this.session },
      cssClass: 'open-invitation-link-menu',
      event: click,
    });
    await menu.present();
    await this.analyticsService.track('opened participant invitation link');
  }

  /**
   * opens the support article to help understand the iphone and ipad beta
   */
  async learnMoreiPhoneiPadBeta() {
    this.supportCenterService.topic('systemRequirements');
  }

  /**
   * Sets up the user and initializes its dependencies
   */
  async setupUser() {
    const sub = this.userService.activeUser$.subscribe((user) => {
      this.user = user;
    });
    this.subs.push(sub);
  }

  /**
   * Sets up the session and initializes the conference
   */
  setupSession() {
    const sub = this.sessionsService.studioSession$.subscribe((session) => {
      this.session = session;
    });

    this.subs.push(sub);
  }

  setupRecordingCount() {
    const sub = this.sessionsService.studioSession$
      .pipe(
        switchMap((session) => {
          if (!session) return [];
          return this.sessionsService.getAllRecordingsInSession(session.sessionID);
        })
      )
      .subscribe((recordings) => {
        const ready = recordings.filter(
          (recording) =>
            !recording.preview &&
            !recording.screenRecording &&
            (recording.percentage === 100 || recording.videoPercentage === 100)
        );
        if (ready.length) this.hasRecordings = true;
      });

    this.subs.push(sub);
  }

  /**
   * Sets up the linked to descript state
   */
  setupLinkedToDescript() {
    const sub = this.descriptService.descriptDrive$.subscribe((drive) => {
      this.linkedToDescript = drive?.linkStatus === 'COMPLETE';
    });

    this.subs.push(sub);
  }

  /**
   * expands minimized participant local participant
   */
  async viewSelf() {
    if (this.dolbyConversation$?.value) {
      this.dolbyService.minimizeLocalParticipant(false);
    } else if (this.dailyCall$?.value) {
      this.dailyService.minimizeLocalParticipant(false);
    }
    await this.analyticsService.track('minimized local participant', { minimized: false });
  }

  /**
   * Leaves the recording session
   */
  async leaveSession(click) {
    if (this.linkedToDescript && this.hasRecordings) {
      const menu = await this.popoverController.create({
        ...defaultPopoverOptions,
        component: LeaveSessionMenuComponent,
        componentProps: { session: this.session, click },
        cssClass: 'leave-session-menu',
        event: click,
      });

      await menu.present();
    } else {
      if (this.dolbyConversation$?.value) {
        await this.dolbyService.leaveConversation();
      } else if (this.dailyCall$?.value) {
        await this.dailyService.leaveCall();
      }
      await this.analyticsService.track('left recording session');
    }
  }

  /**
   * Sets up the recording timer
   */
  setupTimer() {
    const sub = this.recordingService.timer$.subscribe((timer) => {
      if (timer >= 3600) this.timer = dayjs.duration(timer, 'seconds').format('HH:mm:ss');
      else this.timer = dayjs.duration(timer, 'seconds').format('mm:ss');
      // this.timer = dayjs.duration(timer, 'seconds').format('HH:mm:ss');
    });
    this.subs.push(sub);
  }

  setupRecording() {
    const sub = this.recordingService.recording$.subscribe((recording) => {
      this.recording = recording;
    });

    this.subs.push(sub);
  }

  setupWarnings() {
    const sub = combineLatest([this.audioWarnings$, this.videoWarnings$]).subscribe(
      ([audioWarnings, videoWarnings]) => {
        this.warnings = Array.from(audioWarnings).join(', ');
        if (this.warnings && videoWarnings.size) this.warnings += ', ';
        this.warnings += Array.from(videoWarnings).join(', ');
      }
    );
    this.subs.push(sub);
  }

  setupParticipantCounts() {
    if (this.dolbyConversation$?.value) {
      const dolbySub = this.dolbyService.stageParticipants$.subscribe((participants) => {
        this.stageCount = [...participants.keys()].filter((k) => !k.includes('_screen')).length;
        this.updateParticipantTooltip();
      });
      this.subs.push(dolbySub);
    }

    if (this.dailyCall$?.value) {
      const dailySub = this.dailyService.stageParticipants$.subscribe((participants) => {
        this.stageCount = [...participants.keys()].filter((k) => !k.includes('_screen')).length;
        this.updateParticipantTooltip();
      });
      this.subs.push(dailySub);
    }

    const backstageSub = this.dolbyService.backstageParticipants$.subscribe((participants) => {
      this.backstageCount = participants.length;
      this.updateParticipantTooltip();
    });
    this.subs.push(backstageSub);
  }

  updateParticipantTooltip() {
    this.participantsTooltip = `${this.stageCount} on stage`;
    if (this.backstageCount) this.participantsTooltip += ` and ${this.backstageCount} backstage`;
  }

  async openBetaFeedback() {
    const modal = await this.modalController.create({
      component: BetaFeedbackPage,
      componentProps: {
        user: this.user,
      },
      showBackdrop: true,
      backdropDismiss: true,
      animated: true,
      cssClass: 'beta-feedback-modal',
    });

    await modal.present();
    await this.analyticsService.track('opened beta feedback');
  }

  /**
   * Unsubscribe from all observables
   */
  ngOnDestroy() {
    this.subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
